import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BannerState } from 'projects/shared/lib/components/shared/banner/state/banner-state.model';
import { Breadcrumb } from 'projects/shared/lib/components/shared/breadcrumb/breadcrumb.models';
import { LoginBaseComponent } from 'projects/shared/lib/components/standalone/login/login-base.component';
import { AuthenticationError } from 'projects/shared/lib/error/authentication-error';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { IUserLoginCustomerSelectDialogService } from 'projects/shared/lib/interfaces/services/dialogs/user-login-customer-select-dialog-service.abstract';
import { EError } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { CookieBannerComponent } from 'shared/components/shared/base/cookie/banner/cookie-banner.component';
import { DirectivesModule } from 'shared/directives/directives.module';
import { IBannerService, IBannerServiceToken } from 'shared/services/ui/banner/banner-service.interface';
import { ContentComponent } from '../../shared/content/content/content.component';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ContentComponent,
    DirectivesModule,
    CookieBannerComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent extends LoginBaseComponent implements OnInit, OnDestroy {
  private readonly bannerService: IBannerService = inject(IBannerServiceToken);

  private cookieAcceptanceStateSubscription: Subscription | undefined;
  public cookiesAreAccepted: boolean;

  public constructor() {
    super();

    this.cookiesAreAccepted = this.authService.hasAcceptedCookieTerms();
  }

  public ngOnInit(): void {
    this.bannerService.bannerStateSet(new BannerState({
      title: $localize`:@@LoginBannerTitle:Login`,
      breadcrumbItemList: [
        new Breadcrumb({
          text: $localize`:@@LoginBreadcrumbText:Login`,
          routerLink: '/login'
        })
      ]
    }));
    this.cookieAcceptanceStateSubscriptionInit();
  }

  private cookieAcceptanceStateSubscriptionInit() {
    if (!this.cookieAcceptanceStateSubscription) {
      this.cookieAcceptanceStateSubscription = this.authService.cookieTermsAcceptanceStateGet().subscribe(
        (cookieAreAccepted) => {
          this.cookiesAreAccepted = cookieAreAccepted;
          if (!cookieAreAccepted) {
            this.cookieBannerService.cookieBannerShow();
          }
        }
      );
    }
  }

  public ngOnDestroy(): void {
    this.bannerService.bannerStateSet(undefined);
    this.cookieBannerService.cookieBannerHide();
    this.cookieAcceptanceStateSubscription?.unsubscribe();
  }

  public async loginClickAsync(): Promise<void> {
    let cookieAcceptanceState: boolean = this.authService.hasAcceptedCookieTerms();

    if (this.loginRequiresCookieAcceptance && cookieAcceptanceState == false) {
      this.cookieBannerService.cookieBannerShow();
      throw new BasicError($localize`:@@LoginMissingCookieConsentMessage:Please accept the necessary cookies before your next login attempt.`, ErrorCodeService.formatErrorCode(EError.F200130), 4);
    }

    let form = document.getElementById('loginForm') as HTMLFormElement;
    if (form.checkValidity() === false) {
      form.classList.add('was-validated');
      return;
    }

    firstValueFrom(this.userService.userLogin(this.customerNumber, this.coUserOrEmailOrAlias, this.password)).then((accessJWT) => {
      if (accessJWT == undefined) {
        throw new AuthenticationError($localize`:@@LoginErrorJWTMissing:Your login attempt failed. No Authentication data received.`, ErrorCodeService.formatErrorCode(EError.F200101), 4);
      }

      let isLoginSuccessfull = this.authService.isAccessJWTValid(accessJWT);
      if (isLoginSuccessfull != true) {
        throw new AuthenticationError($localize`:@@LoginErrorJWTInvalid:Login failed. Authentication data is invalid.`, ErrorCodeService.formatErrorCode(EError.F200111), 4);
      }

      this.authService.accessJWTSet(accessJWT);

      setTimeout(() => {
        firstValueFrom(this.userService.userFavoritesInitialize());
      });

      let isMultiCustomerUser = this.authService.isMultiCustomerUser();
      if (isMultiCustomerUser == true) {
        setTimeout(() => {
          this.customerSelectDialogShow();
          return;
        });
      }

      this.router.navigate([this.authorizedLandingPage.routerlink], { queryParams: this.authorizedLandingPage.queryParams });
    }).catch((ex) => {
      throw new AuthenticationError($localize`:@@LoginErrorJWTInvalid:Login failed. Authentication data is invalid.`, JSON.parse(ex.response).errorCode, 4);
    });
  }

  public customerSelectDialogShow() {
    ServiceLocator.injector.get(IUserLoginCustomerSelectDialogService).userLoginCustomerSelectDialogShow(
      () => { },
    );
  }

}
